import { createContext, useContext, useCallback, useState, useEffect, useRef, type ReactNode } from "react";
import type { Instrument } from "@/components/drumpattern/types";
import { loadMidiMapping, type MidiNoteMap } from "@/components/drumpattern/midiMapping";

export interface MidiHit {
  instrument: Instrument;
  velocity: number;
  timestamp: number;
  note: number;
}

export type MidiRawMessageType = "noteOn" | "noteOff" | "controlChange" | "aftertouch" | "unknown";

export interface MidiRawMessage {
  type: MidiRawMessageType;
  status: number;
  command: number;
  channel: number;
  data: number[];
  timestamp: number;
  note?: number;
  velocity?: number;
  controller?: number;
  value?: number;
  mappedInstrument?: Instrument;
}

interface MidiContextValue {
  midiSupported: boolean;
  connected: boolean;
  deviceName: string | null;
  lastHit: MidiHit | null;
  lastRawMessage: MidiRawMessage | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  mapping: MidiNoteMap;
  setMapping: (map: MidiNoteMap) => void;
  availableInputs: { id: string; name: string | null }[];
  selectInput: (id: string) => void;
  /** Register a callback that receives every hit while mounted */
  subscribeHit: (cb: (hit: MidiHit) => void) => () => void;
  /** Register a callback that receives every raw MIDI message while mounted */
  subscribeRawMidiMessage: (cb: (message: MidiRawMessage) => void) => () => void;
}

const MidiContext = createContext<MidiContextValue | null>(null);

function parseRawMidiMessage(data: Uint8Array, mapping: MidiNoteMap): MidiRawMessage {
  const [status = 0, data1 = 0, data2 = 0] = data;
  const command = status & 0xf0;
  const channel = (status & 0x0f) + 1;
  const base = {
    status,
    command,
    channel,
    data: Array.from(data),
    timestamp: performance.now(),
  };

  if (command === 0x90) {
    const mappedInstrument = mapping[data1] as Instrument | undefined;
    return {
      ...base,
      type: data2 > 0 ? "noteOn" : "noteOff",
      note: data1,
      velocity: data2,
      mappedInstrument,
    };
  }

  if (command === 0x80) {
    const mappedInstrument = mapping[data1] as Instrument | undefined;
    return {
      ...base,
      type: "noteOff",
      note: data1,
      velocity: data2,
      mappedInstrument,
    };
  }

  if (command === 0xb0) {
    return {
      ...base,
      type: "controlChange",
      controller: data1,
      value: data2,
    };
  }

  if (command === 0xa0) {
    const mappedInstrument = mapping[data1] as Instrument | undefined;
    return {
      ...base,
      type: "aftertouch",
      note: data1,
      value: data2,
      mappedInstrument,
    };
  }

  if (command === 0xd0) {
    return {
      ...base,
      type: "aftertouch",
      value: data1,
    };
  }

  return {
    ...base,
    type: "unknown",
    value: data1,
  };
}

export function useMidiContext() {
  const ctx = useContext(MidiContext);
  if (!ctx) throw new Error("useMidiContext must be used within MidiProvider");
  return ctx;
}

export function MidiProvider({ children }: { children: ReactNode }) {
  const midiSupported = typeof navigator !== "undefined" && "requestMIDIAccess" in navigator;
  const [connected, setConnected] = useState(false);
  const [deviceName, setDeviceName] = useState<string | null>(null);
  const [lastHit, setLastHit] = useState<MidiHit | null>(null);
  const [lastRawMessage, setLastRawMessage] = useState<MidiRawMessage | null>(null);
  const [mapping, setMappingState] = useState<MidiNoteMap>(loadMidiMapping);
  const [availableInputs, setAvailableInputs] = useState<{ id: string; name: string | null }[]>([]);

  const mappingRef = useRef(mapping);
  const accessRef = useRef<MIDIAccess | null>(null);
  const activeInputRef = useRef<MIDIInput | null>(null);
  const subscribersRef = useRef<Set<(hit: MidiHit) => void>>(new Set());
  const rawSubscribersRef = useRef<Set<(message: MidiRawMessage) => void>>(new Set());

  useEffect(() => { mappingRef.current = mapping; }, [mapping]);

  const handleMessage = useCallback((e: MIDIMessageEvent) => {
    const rawMessage = parseRawMidiMessage(e.data, mappingRef.current);
    setLastRawMessage(rawMessage);
    rawSubscribersRef.current.forEach((cb) => cb(rawMessage));

    if (rawMessage.type === "noteOn" && rawMessage.note !== undefined && rawMessage.velocity !== undefined) {
      const instrument = rawMessage.mappedInstrument;
      if (!instrument) return; // Skip unmapped notes for playback/feedback hits
      const hit: MidiHit = {
        instrument,
        velocity: rawMessage.velocity,
        timestamp: rawMessage.timestamp,
        note: rawMessage.note,
      };
      setLastHit(hit);
      subscribersRef.current.forEach((cb) => cb(hit));
    }
  }, []);

  const activateInput = useCallback((input: MIDIInput) => {
    if (activeInputRef.current) activeInputRef.current.onmidimessage = null;
    input.onmidimessage = handleMessage;
    activeInputRef.current = input;
    const name = input.name ?? input.id;
    setDeviceName(name);
    setConnected(true);
    try { localStorage.setItem("drumlog_midi_device", name); } catch {
      // Ignore unavailable localStorage in restricted browser contexts.
    }
  }, [handleMessage]);

  const selectInput = useCallback((id: string) => {
    const access = accessRef.current;
    if (!access) return;
    const input = Array.from(access.inputs.values()).find((i) => i.id === id);
    if (input) activateInput(input);
  }, [activateInput]);

  const connect = useCallback(async () => {
    if (!midiSupported) return;
    try {
      const access = await navigator.requestMIDIAccess();
      accessRef.current = access;
      const inputs = Array.from(access.inputs.values());
      setAvailableInputs(inputs.map((i) => ({ id: i.id, name: i.name })));
      if (inputs.length === 0) return;
      // Prefer the last used device
      let savedName: string | null = null;
      try { savedName = localStorage.getItem("drumlog_midi_device"); } catch {
        // Ignore unavailable localStorage in restricted browser contexts.
      }
      const preferred = savedName ? inputs.find((i) => i.name === savedName) : null;
      activateInput(preferred ?? inputs[0]);
    } catch (err) {
      console.error("MIDI access denied", err);
    }
  }, [midiSupported, activateInput]);

  // Auto-reconnect on mount if a device was previously used
  useEffect(() => {
    let savedName: string | null = null;
    try { savedName = localStorage.getItem("drumlog_midi_device"); } catch {
      // Ignore unavailable localStorage in restricted browser contexts.
    }
    if (savedName && midiSupported) {
      connect();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const disconnect = useCallback(() => {
    if (activeInputRef.current) {
      activeInputRef.current.onmidimessage = null;
      activeInputRef.current = null;
    }
    setConnected(false);
    setDeviceName(null);
    try { localStorage.removeItem("drumlog_midi_device"); } catch {
      // Ignore unavailable localStorage in restricted browser contexts.
    }
  }, []);

  const setMapping = useCallback((map: MidiNoteMap) => {
    setMappingState(map);
    mappingRef.current = map;
  }, []);

  const subscribeHit = useCallback((cb: (hit: MidiHit) => void) => {
    subscribersRef.current.add(cb);
    return () => { subscribersRef.current.delete(cb); };
  }, []);

  const subscribeRawMidiMessage = useCallback((cb: (message: MidiRawMessage) => void) => {
    rawSubscribersRef.current.add(cb);
    return () => { rawSubscribersRef.current.delete(cb); };
  }, []);

  return (
    <MidiContext.Provider value={{
      midiSupported, connected, deviceName, lastHit, lastRawMessage,
      connect, disconnect, mapping, setMapping,
      availableInputs, selectInput, subscribeHit, subscribeRawMidiMessage,
    }}>
      {children}
    </MidiContext.Provider>
  );
}
